import { Alert, BillIcon, Box, CashIcon, PettyCashBudgetIcon } from '@razorpay/blade/components';

import React from 'react';
import { CtcBreakdownDataForEmployeeOnboarding } from '../../types';

import { CTCBreakdownTable } from './CTCBreakdownTable';
import { ComponentHeader } from './ContentHeader';
import { TableSkeletonBody } from 'components/ui/TableSkeletonBody/TableSkeletonBody';

type CtcPreviewProps = {
  ctcData: CtcBreakdownDataForEmployeeOnboarding | undefined;
  isLoadingOrFetching?: boolean;
  compensationError: boolean;
  refetchCompensationData: () => void;
  showCalculations?: boolean;
};

const CTCPreview = ({
  ctcData,
  isLoadingOrFetching,
  compensationError,
  refetchCompensationData,
  showCalculations = true,
}: CtcPreviewProps) => {
  if (compensationError) {
    return (
      <Alert
        description={
          'We are facing some unknown error to fetch compensation data. Please try again.'
        }
        color="negative"
        isDismissible={false}
        actions={{
          primary: {
            text: 'Retry',
            onClick: refetchCompensationData,
          },
        }}
        testID="compensation-breakdown-error-alert"
      />
    );
  }
  return (
    <Box display="flex" flexDirection="column" gap={'spacing.8'}>
      {ctcData?.fixed?.data && ctcData?.fixed?.data?.length > 0 ? (
        <Box>
          {isLoadingOrFetching ? (
            <TableSkeletonBody rowCount={6} columnCount={3} />
          ) : (
            <>
              <ComponentHeader
                icon={<CashIcon size="medium" color="surface.icon.onSea.onSubtle" />}
                iconBackground={'surface.background.sea.subtle'}
                title={'Fixed'}
              />

              <CTCBreakdownTable
                columnTitles={showCalculations ? ['Component', 'Calculations', 'Yearly Amount'] : ['Component', 'Yearly Amount']}
                data={ctcData.fixed}
                showCalculations={showCalculations}
                showTotal={true}
              />
            </>
          )}
        </Box>
      ) : null}

      {ctcData?.variable?.data && ctcData?.variable?.data?.length ? (
        <Box>
          {isLoadingOrFetching ? (
            <TableSkeletonBody rowCount={3} columnCount={3} />
          ) : (
            <>
              <ComponentHeader
                icon={<PettyCashBudgetIcon size="medium" color="surface.icon.primary.normal" />}
                iconBackground={'surface.background.primary.subtle'}
                title={'Variable'}
              />

              <CTCBreakdownTable
                columnTitles={showCalculations ? ['Component', 'Calculations', 'Yearly Amount'] : ['Component', 'Yearly Amount']}
                data={ctcData.variable}
                showCalculations={showCalculations}
              />
            </>
          )}
        </Box>
      ) : null}

      {ctcData?.otherCtc?.data && ctcData?.otherCtc?.data?.length > 0 ? (
        <Box>
          {isLoadingOrFetching ? (
            <TableSkeletonBody rowCount={3} columnCount={3} />
          ) : (
            <>
              <ComponentHeader
                icon={<BillIcon size="medium" color="feedback.icon.notice.intense" />}
                iconBackground={'surface.background.gray.moderate'}
                title={'Other CTC component'}
              />

              <CTCBreakdownTable
                columnTitles={['Component', 'Yearly Amount']}
                data={ctcData.otherCtc}
                showCalculations={false}
              />
            </>
          )}
        </Box>
      ) : null}
    </Box>
  );
};

export { CTCPreview };
